import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  CurrentUser,
  clearStoredToken,
  fetchCurrentUser,
  initAuthToken,
  changePassword as apiChangePassword,
  signIn as apiSignIn,
  signOut as apiSignOut,
} from '../api/auth';

type AuthContextValue = {
  user: CurrentUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string, newPasswordConfirmation: string) => Promise<void>;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    const token = initAuthToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const current = await fetchCurrentUser();
      setUser(current);
    } catch {
      clearStoredToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const signIn = async (email: string, password: string) => {
    const current = await apiSignIn(email, password);
    setUser(current);
  };

  const signOut = async () => {
    try {
      await apiSignOut();
    } finally {
      setUser(null);
    }
  };

  const changePassword = async (
    currentPassword: string,
    newPassword: string,
    newPasswordConfirmation: string
  ) => {
    await apiChangePassword(currentPassword, newPassword, newPasswordConfirmation);
  };

  const value = useMemo(
    () => ({
      user,
      loading,
      signIn,
      signOut,
      changePassword,
      refresh,
    }),
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};
